import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAuth } from '../../hooks/useAuth';
import { gerarCaracteristicasPorNivel, nivelParaNota } from '../../utils/playerUtils';

interface PlayerFormProps {
  groupId: string;
  playerId?: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

const PlayerForm: React.FC<PlayerFormProps> = ({ groupId, playerId, onCancel, onSuccess }) => {
  const { supabase } = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [idade, setIdade] = useState('');
  const [tipo, setTipo] = useState('ativo');
  const [posicao, setPosicao] = useState('');
  const [referencia, setReferencia] = useState('');
  const [telefone, setTelefone] = useState('');
  const [countryCode, setCountryCode] = useState('+55');
  const [nivel, setNivel] = useState(3);
  const [avatarColor] = useState(() => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  });

  const formatPhoneNumber = (value: string, countryCode: string): string => {
    const numbers = value.replace(/\D/g, '');
    
    if (countryCode === '+55') {
      // Brazilian format: (11) 99999-9999
      if (numbers.length <= 11) {
        return numbers
          .replace(/(\d{2})(\d)/, '($1) $2')
          .replace(/(\d{4,5})(\d{4})$/, '$1-$2');
      }
    } else if (countryCode === '+1') {
      // US format: (555) 123-4567
      if (numbers.length <= 10) {
        return numbers
          .replace(/(\d{3})(\d)/, '($1) $2')
          .replace(/(\d{3})(\d{4})$/, '$1-$2');
      }
    } else {
      // European format: simple grouping
      if (numbers.length <= 12) {
        return numbers
          .replace(/(\d{2,3})(\d{3})(\d{3,4})$/, '$1 $2 $3');
      }
    }
    
    return numbers;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTelefone(formatPhoneNumber(e.target.value, countryCode));
  };

  const getPhonePlaceholder = (countryCode: string) => {
    switch (countryCode) {
      case '+55':
        return '(11) 99999-9999';
      case '+1':
        return '(555) 123-4567';
      case '+34':
      case '+33':
      case '+49':
      case '+39':
      case '+44':
      case '+31':
      case '+32':
      case '+41':
      case '+43':
      case '+351':
        return '12 345 6789';
      default:
        return '123456789';
    }
  };

  const validateForm = () => {
    if (!nome.trim()) return 'Nome é obrigatório';
    if (!idade || parseInt(idade) <= 0) return 'Idade inválida';
    if (!posicao) return 'Posição é obrigatória';
    if (!telefone) return 'Telefone é obrigatório';
    return null;
  };

  useEffect(() => {
    if (!playerId) return;
    const fetchPlayer = async () => {
      try {
        const { data, error } = await supabase
          .from('jogadores')
          .select('*')
          .eq('id', playerId)
          .single();
        if (error) throw error;
        if (data) {
          setNome(data.nome);
          setIdade(data.idade?.toString() || '');
          setTipo(data.tipo || 'ativo');
          setPosicao(data.posicao || '');
          setReferencia(data.referencia || '');
          
          // Parse existing phone number to extract country code and number
          if (data.telefone) {
            const phone = data.telefone;
            if (phone.startsWith('+')) {
              // Extract country code from international format
              const match = phone.match(/^(\+\d{1,4})\s*(.+)$/);
              if (match) {
                setCountryCode(match[1]);
                setTelefone(formatPhoneNumber(match[2], match[1]));
              } else {
                setTelefone(phone);
              }
            } else {
              setTelefone(phone);
            }
          }
          setNivel(data.nivel);
        }
      } catch (err) {
        console.error('Erro ao carregar jogador:', err);
        setError('Erro ao carregar dados do jogador');
      }
    };
    fetchPlayer();
  }, [playerId, supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format phone number for storage (international format)
      const cleanPhone = telefone.replace(/\D/g, '');
      const formattedPhoneForStorage = `${countryCode} ${cleanPhone}`;
      
      const playerData = {
        nome: nome.trim(),
        idade: parseInt(idade),
        tipo,
        posicao,
        referencia: referencia.trim() || null,
        telefone: formattedPhoneForStorage,
        nivel,
        grupo_id: groupId,
      };

      // ✅ Converte nível para nota antes de gerar características
      const nota = nivelParaNota(nivel);
      const caracteristicasAtualizadas = gerarCaracteristicasPorNivel(nota);

      if (playerId) {
        const { error: updateError } = await supabase
          .from('jogadores')
          .update(playerData)
          .eq('id', playerId);
        if (updateError) throw updateError;

        const { data: existingChar } = await supabase
          .from('jogadores_caracteristicas')
          .select('*')
          .eq('jogador_id', playerId)
          .maybeSingle();

        if (existingChar) {
          const { error: charError } = await supabase
            .from('jogadores_caracteristicas')
            .update(caracteristicasAtualizadas)
            .eq('jogador_id', playerId);
          if (charError) throw charError;
        } else {
          const { error: charError } = await supabase
            .from('jogadores_caracteristicas')
            .insert({ jogador_id: playerId, ...caracteristicasAtualizadas });
          if (charError) throw charError;
        }

      } else {
        const { data: insertedPlayer, error: insertError } = await supabase
          .from('jogadores')
          .insert(playerData)
          .select()
          .single();
        if (insertError) throw insertError;

        const { error: charError } = await supabase
          .from('jogadores_caracteristicas')
          .insert({ jogador_id: insertedPlayer.id, ...caracteristicasAtualizadas });
        if (charError) throw charError;
      }

      onSuccess?.();
      onCancel();
    } catch (err) {
      console.error('Erro ao salvar jogador:', err);
      setError('Erro ao salvar jogador');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Para preview das características ao mudar o nível
  const previewCaracteristicas = gerarCaracteristicasPorNivel(nivelParaNota(nivel));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">{error}</div>}

      <div className={`w-32 h-32 rounded-full flex items-center justify-center text-3xl text-white mx-auto ${avatarColor}`}>
        {nome.charAt(0).toUpperCase() || <User className="w-10 h-10" />}
      </div>

      <div>
        <label htmlFor="nome" className="block text-sm font-medium mb-1">Nome Completo *</label>
        <input type="text" id="nome" className="input" value={nome} onChange={(e) => setNome(e.target.value)} required />
      </div>

      <div>
        <label htmlFor="idade" className="block text-sm font-medium mb-1">Idade *</label>
        <input type="number" id="idade" className="input" value={idade} onChange={(e) => setIdade(e.target.value)} min="1" required />
      </div>

      <div>
        <label htmlFor="tipo" className="block text-sm font-medium mb-1">Tipo de Jogador *</label>
        <select id="tipo" className="input" value={tipo} onChange={(e) => setTipo(e.target.value)} required>
          <option value="ativo">Ativo</option>
          <option value="avulso">Avulso</option>
        </select>
      </div>

      <div>
        <label htmlFor="posicao" className="block text-sm font-medium mb-1">Posição *</label>
        <select id="posicao" className="input" value={posicao} onChange={(e) => setPosicao(e.target.value)} required>
          <option value="">Selecione uma posição</option>
          <option value="goleiro">Goleiro</option>
          <option value="zagueiro">Zagueiro</option>
          <option value="lateral">Lateral</option>
          <option value="meio-campo">Meio Campo</option>
          <option value="atacante">Atacante</option>
        </select>
      </div>

      <div>
        <label htmlFor="nivel" className="block text-sm font-medium mb-1">Nível *</label>
        <select id="nivel" className="input" value={nivel} onChange={(e) => setNivel(parseInt(e.target.value))} required>
          <option value="1">1 - Iniciante</option>
          <option value="2">2 - Básico</option>
          <option value="3">3 - Intermediário</option>
          <option value="4">4 - Avançado</option>
          <option value="5">5 - Profissional</option>
        </select>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {Object.entries(previewCaracteristicas).map(([key, value]) => (
            <div key={key} className="flex justify-between text-sm">
              <span>{key}</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="referencia" className="block text-sm font-medium mb-1">Referência / Responsável (opcional)</label>
        <input type="text" id="referencia" className="input" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Ex: Indicado por João" />
      </div>

      <div>
        <label htmlFor="telefone" className="block text-sm font-medium mb-1">Telefone (WhatsApp) *</label>
        <div className="flex gap-2">
          <select
            className="input w-32"
            value={countryCode}
            onChange={(e) => {
              setCountryCode(e.target.value);
              // Reformat phone number when country code changes
              if (telefone) {
                setTelefone(formatPhoneNumber(telefone.replace(/\D/g, ''), e.target.value));
              }
            }}
          >
            <option value="+55">🇧🇷 +55</option>
            <option value="+1">🇺🇸 +1</option>
            <option value="+54">🇦🇷 +54</option>
            <option value="+56">🇨🇱 +56</option>
            <option value="+57">🇨🇴 +57</option>
            <option value="+51">🇵🇪 +51</option>
            <option value="+58">🇻🇪 +58</option>
            <option value="+598">🇺🇾 +598</option>
            <option value="+595">🇵🇾 +595</option>
            <option value="+591">🇧🇴 +591</option>
            <option value="+593">🇪🇨 +593</option>
            <option value="+34">🇪🇸 +34</option>
            <option value="+33">🇫🇷 +33</option>
            <option value="+49">🇩🇪 +49</option>
            <option value="+39">🇮🇹 +39</option>
            <option value="+44">🇬🇧 +44</option>
            <option value="+31">🇳🇱 +31</option>
            <option value="+32">🇧🇪 +32</option>
            <option value="+41">🇨🇭 +41</option>
            <option value="+43">🇦🇹 +43</option>
            <option value="+351">🇵🇹 +351</option>
          </select>
          <input
            type="tel"
            id="telefone"
            className="input flex-1"
            value={telefone}
            onChange={handlePhoneChange}
            placeholder={getPhonePlaceholder(countryCode)}
            required
          />
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Número completo com código do país para WhatsApp
        </p>
      </div>

      <div className="flex gap-4">
        <button type="button" onClick={onCancel} className="btn-outline flex-1" disabled={loading}>Cancelar</button>
        <button type="submit" className="btn-primary flex-1" disabled={loading}>{loading ? 'Salvando...' : (playerId ? 'Atualizar' : 'Salvar')}</button>
      </div>
    </form>
  );
};

export default PlayerForm;
